import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, CreditCard, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { money } from "@/lib/currency";
import { euDate } from "@/lib/utils";
import { RsvpConfirmedCard } from "@/components/site/RsvpConfirmedCard";

const SUGGESTED_NOTE_DEFAULT = {
  en: "Your contribution helps us host more events like this.",
  he: "התרומה שלך עוזרת לנו לקיים עוד אירועים כמו זה.",
  ru: "Ваш взнос помогает нам проводить больше подобных мероприятий.",
};

const PARTS_L = {
  title: { en: "Which parts will you join?", cs: "Kterých částí se zúčastníte?", he: "לאילו חלקים תגיעו?", ru: "В каких частях вы примете участие?" },
  required: { en: "Please choose at least one option", cs: "Vyberte prosím alespoň jednu možnost", he: "אנא בחרו לפחות אפשרות אחת", ru: "Выберите хотя бы один вариант" },
  full: { en: "Fully booked", cs: "Obsazeno", he: "מלא", ru: "Мест нет" },
  left: { en: "{n} spots left", cs: "Zbývá {n} míst", he: "נותרו {n} מקומות", ru: "Осталось {n} мест" },
};

export function RsvpForm({ event, accessKey = "" }) {
  const { t, lang } = useLang();
  const parts = (event.parts || []).filter((p) => p && p.id);
  const [selectedParts, setSelectedParts] = useState([]);
  const chosenParts = parts.filter((p) => selectedParts.includes(p.id));
  const partsAdult = chosenParts.reduce((a, p) => a + (Number(p.price_adult) || 0), 0);
  const partsChild = chosenParts.reduce((a, p) => a + (Number(p.price_child) || 0), 0);
  const partsPriced = parts.some((p) => (Number(p.price_adult) || 0) > 0 || (Number(p.price_child) || 0) > 0);
  const adultP = partsPriced ? partsAdult : (Number(event.price_adult) || Number(event.price_per_person) || 0);
  const childP = partsPriced ? partsChild : (Number(event.price_child) || 0);
  const pf = Number(event.price_family) || 0;
  const hasPer = adultP > 0 || childP > 0 || partsPriced;
  const hasFamily = pf > 0;
  const isPaid = hasPer || hasFamily;
  const mode = event.payment_mode || (isPaid ? "required" : "none");
  const isRequired = mode === "required" && isPaid;
  const isSuggested = mode === "suggested";
  const L = {
    title: { en: "Suggested participation", cs: "Doporučený příspěvek", he: "השתתפות מוצעת", ru: "Рекомендуемый взнос" }[lang] || "Suggested participation",
    word: { en: "Suggested", cs: "Doporučeno", he: "מוצע", ru: "Рекомендуется" }[lang] || "Suggested",
    hint: { en: "Optional — change the amount or RSVP without contributing.", cs: "Nepovinné — změňte částku nebo se zaregistrujte bez příspěvku.", he: "רשות — ניתן לשנות את הסכום או להירשם ללא תשלום.", ru: "Необязательно — измените сумму или зарегистрируйтесь без взноса." }[lang] || "Optional — change the amount or RSVP without contributing.",
    contribute: { en: "Contribute & RSVP", cs: "Přispět a zaregistrovat se", he: "לתרום ולהירשם", ru: "Внести и записаться" }[lang] || "Contribute & RSVP",
    without: { en: "RSVP without contributing", cs: "Registrace bez příspěvku", he: "הרשמה ללא תשלום", ru: "Записаться без взноса" }[lang] || "RSVP without contributing",
  };

  const [form, setForm] = useState({ name: "", email: "", phone: "", adults: 1, children: 0 });
  const [ticket, setTicket] = useState(hasPer ? "per_person" : "family");
  const [contribution, setContribution] = useState("");
  const [contribTouched, setContribTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [fields, setFields] = useState([]);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [payLoading, setPayLoading] = useState("");
  const [cur, setCur] = useState("czk");
  const [phoneRequired, setPhoneRequired] = useState(true);
  const [suggestedNote, setSuggestedNote] = useState({});

  useEffect(() => {
    api.get("/rsvp-fields").then((r) => {
      setFields(r.data.custom_fields || []);
      setPhoneRequired(r.data.phone_required !== false);
      setSuggestedNote(r.data.suggested_note || {});
    }).catch(() => {});
    api.get("/payments/config").then((r) => {
      setPaypalEnabled(!!r.data.paypal_enabled);
      setCur(r.data.event_currency || "czk");
    }).catch(() => {});
  }, []);

  const showEmail = event.rsvp_show_email !== false;
  const emailReq = event.rsvp_email_required !== false;
  const showPhone = event.rsvp_show_phone !== false;
  const showAdults = event.rsvp_show_adults !== false;
  const showChildren = event.rsvp_show_children !== false;
  const allFields = [...fields, ...(event.custom_questions || [])];
  const adults = showAdults ? Math.max(1, Number(form.adults) || 1) : 1;
  const children = showChildren ? Math.max(0, Number(form.children) || 0) : 0;
  const guests = adults + children;
  const amount = ticket === "family" ? pf : adults * adultP + children * childP;
  const customPresets = (event.suggested_amounts || []).map(Number).filter((n) => n > 0);
  const presetBase = amount > 0 ? amount : 100;
  const autoPresets = Array.from(new Set([presetBase, presetBase * 2, presetBase * 3].map((n) => Math.round(n)))).filter((n) => n > 0);
  const presets = customPresets.length ? customPresets : autoPresets;
  const defaultContrib = customPresets.length ? customPresets[0] : amount;
  const contribValue = contribTouched ? Math.max(0, Number(contribution) || 0) : defaultContrib;
  const setContrib = (v) => { setContribTouched(true); setContribution(v); };
  const payNow = isRequired || (isSuggested && contribValue > 0);
  const phoneReq = event.rsvp_phone_required !== false && phoneRequired;

  const setAnswer = (label, value) => setAnswers((a) => ({ ...a, [label]: value }));

  const qLabel = (f) => (lang !== "en" && f.i18n?.[lang]?.label) ? f.i18n[lang].label : f.label;
  const qOption = (f, o, i) => {
    const tr = lang !== "en" ? f.i18n?.[lang]?.options : null;
    return (tr && tr[i]) ? tr[i] : o;
  };
  const yesNo = { yes: { en: "Yes", cs: "Ano", he: "כן", ru: "Да" }[lang] || "Yes", no: { en: "No", cs: "Ne", he: "לא", ru: "Нет" }[lang] || "No" };

  const computeErrors = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (showEmail && emailReq && !form.email.trim()) e.email = true;
    if (parts.length > 0 && selectedParts.length === 0) e.parts = true;
    if (showPhone && phoneReq && !form.phone.trim()) e.phone = true;
    for (const f of allFields) {
      if (!f.required) continue;
      const v = answers[f.label];
      if (f.type === "checkbox") {
        if (v !== "yes" && v !== "no") e[f.id] = true;
      } else if (v === undefined || v === null || String(v).trim() === "") {
        e[f.id] = true;
      }
    }
    return e;
  };

  const clearError = (key) => setErrors((prev) => {
    if (!prev[key]) return prev;
    const n = { ...prev }; delete n[key]; return n;
  });

  const submit = async (e, forceFree = false) => {
    e.preventDefault();
    const errs = computeErrors();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t("rsvp.check_form"));
      setTimeout(() => {
        const el = document.querySelector('[data-invalid="true"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    const pay = !forceFree && payNow;
    setSubmitting(true);
    try {
      if (pay) {
        const { data } = await api.post("/rsvps/checkout", {
          event_id: event.id,
          name: form.name,
          email: showEmail ? form.email.trim() : "",
          phone: form.phone,
          guests,
          adults,
          children,
          lang,
          ticket_type: ticket,
          answers,
          parts: selectedParts,
          amount: isSuggested ? contribValue : undefined,
          origin_url: window.location.origin,
          access_key: accessKey,
        });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await api.post("/rsvps", {
        event_id: event.id,
        name: form.name,
        email: showEmail ? form.email.trim() : "",
        phone: form.phone,
        guests,
        adults,
        children,
        lang,
        answers,
        parts: selectedParts,
        access_key: accessKey,
      });
      if (data?.status === "approved") { setConfirmed(data); setDone(true); return; }
      setDone(true);
      toast.success(t("rsvp.pending_toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("rsvp.error"));
      setSubmitting(false);
    }
  };

  const payPaypal = async () => {
    const errs = computeErrors();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t("rsvp.check_form"));
      setTimeout(() => {
        const el = document.querySelector('[data-invalid="true"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setSubmitting(true);
    setPayLoading("paypal");
    try {
      const { data } = await api.post("/rsvps/paypal/create", {
        event_id: event.id,
        name: form.name,
        email: showEmail ? form.email.trim() : "",
        phone: form.phone,
        guests,
        adults,
        children,
        lang,
        ticket_type: ticket,
        answers,
        parts: selectedParts,
        amount: isSuggested ? contribValue : undefined,
        origin_url: window.location.origin,
        access_key: accessKey,
      });
      window.location.href = data.approve_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("rsvp.error"));
      setSubmitting(false);
      setPayLoading("");
    }
  };

  if (done && confirmed) {
    return <RsvpConfirmedCard event={event} rsvp={confirmed} />;
  }
  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="rsvp-success"
        className="rounded-3xl bg-white border border-[#BE923C]/30 p-8 text-center"
      >
        <span className="mx-auto w-14 h-14 rounded-full bg-[#BE923C]/12 flex items-center justify-center text-[#BE923C]">
          <Clock size={28} />
        </span>
        <h3 className="mt-4 font-serif text-2xl">{t("rsvp.pending_title")}</h3>
        <p className="mt-2 text-[#475569]">{t("rsvp.pending_desc")}</p>
      </motion.div>
    );
  }

  const renderField = (f) => {
    const val = answers[f.label];
    const hasErr = !!errors[f.id];
    const errMsg = hasErr && <p className="mt-1 text-xs text-red-500" data-testid={`rsvp-custom-${f.id}-error`}>{t("rsvp.required")}</p>;
    if (f.type === "select") {
      return (
        <div key={f.id} data-invalid={hasErr ? "true" : undefined}>
          <Label className={hasErr ? "text-red-500" : ""}>{qLabel(f)}{f.required && <span className="text-[#BE923C]"> *</span>}</Label>
          <select
            data-testid={`rsvp-custom-${f.id}`}
            value={val || ""}
            onChange={(e) => { setAnswer(f.label, e.target.value); clearError(f.id); }}
            className={`mt-1.5 w-full rounded-md border bg-white px-3 py-2 text-sm ${hasErr ? "border-red-400" : "border-[#0F172A]/10"}`}
          >
            <option value="">{t("rsvp.select_ph")}</option>
            {(f.options || []).map((o, i) => <option key={i} value={o}>{qOption(f, o, i)}</option>)}
          </select>
          {errMsg}
        </div>
      );
    }
    if (f.type === "checkbox") {
      return (
        <div key={f.id} data-invalid={hasErr ? "true" : undefined}>
          <Label className={hasErr ? "text-red-500" : ""}>{qLabel(f)}{f.required && <span className="text-[#BE923C]"> *</span>}</Label>
          <div className="mt-1.5 flex gap-2">
            {[["yes", yesNo.yes], ["no", yesNo.no]].map(([v, lbl]) => (
              <button
                key={v}
                type="button"
                data-testid={`rsvp-custom-${f.id}-${v}`}
                onClick={() => { setAnswer(f.label, v); clearError(f.id); }}
                className={`flex-1 rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${val === v ? "bg-[#0F172A] text-white border-[#0F172A]" : `bg-white text-[#475569] ${hasErr ? "border-red-400" : "border-[#0F172A]/15"} hover:border-[#0F172A]/40`}`}
              >
                {lbl}
              </button>
            ))}
          </div>
          {errMsg}
        </div>
      );
    }
    return (
      <div key={f.id} data-invalid={hasErr ? "true" : undefined}>
        <Label className={hasErr ? "text-red-500" : ""}>{qLabel(f)}{f.required && <span className="text-[#BE923C]"> *</span>}</Label>
        <Input
          data-testid={`rsvp-custom-${f.id}`}
          value={val || ""}
          onChange={(e) => { setAnswer(f.label, e.target.value); clearError(f.id); }}
          className={`mt-1.5 ${hasErr ? "border-red-400 focus-visible:ring-red-300" : "border-[#0F172A]/10"}`}
        />
        {errMsg}
      </div>
    );
  };

  return (
    <form onSubmit={submit} noValidate data-testid="rsvp-form" className="rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] p-7 sm:p-8 space-y-4">
      <h3 className="font-serif text-2xl">{t("rsvp.title")}</h3>
      <div data-invalid={errors.name ? "true" : undefined}>
        <Label htmlFor="rsvp-name" className={errors.name ? "text-red-500" : ""}>{t("rsvp.fullname")}<span className="text-[#BE923C]"> *</span></Label>
        <Input id="rsvp-name" data-testid="rsvp-name" value={form.name}
          onChange={(e) => { setForm({ ...form, name: e.target.value }); clearError("name"); }}
          className={`mt-1.5 ${errors.name ? "border-red-400 focus-visible:ring-red-300" : "border-[#0F172A]/10"}`} />
        {errors.name && <p className="mt-1 text-xs text-red-500" data-testid="rsvp-name-error">{t("rsvp.required")}</p>}
      </div>
      {showEmail && (
        <div data-invalid={errors.email ? "true" : undefined}>
          <Label htmlFor="rsvp-email" className={errors.email ? "text-red-500" : ""}>{t("rsvp.email")}{emailReq && <span className="text-[#BE923C]"> *</span>}</Label>
          <Input id="rsvp-email" data-testid="rsvp-email" type="email" value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); clearError("email"); }}
            className={`mt-1.5 ${errors.email ? "border-red-400 focus-visible:ring-red-300" : "border-[#0F172A]/10"}`} />
          {errors.email && <p className="mt-1 text-xs text-red-500" data-testid="rsvp-email-error">{t("rsvp.required")}</p>}
        </div>
      )}
      {(showPhone || showAdults || showChildren) && (
        <div className="grid grid-cols-3 gap-4">
          {showPhone && (
            <div data-invalid={errors.phone ? "true" : undefined}>
              <Label htmlFor="rsvp-phone" className={errors.phone ? "text-red-500" : ""}>{phoneReq ? t("rsvp.phone").replace(/\s*\([^)]*\)\s*$/, "") : t("rsvp.phone")}{phoneReq && <span className="text-[#BE923C]"> *</span>}</Label>
              <Input id="rsvp-phone" data-testid="rsvp-phone" value={form.phone}
                onChange={(e) => { setForm({ ...form, phone: e.target.value }); clearError("phone"); }}
                className={`mt-1.5 ${errors.phone ? "border-red-400 focus-visible:ring-red-300" : "border-[#0F172A]/10"}`} />
              {errors.phone && <p className="mt-1 text-xs text-red-500" data-testid="rsvp-phone-error">{t("rsvp.required")}</p>}
            </div>
          )}
          {showAdults && (
            <div>
              <Label htmlFor="rsvp-adults">{t("rsvp.adults")}</Label>
              <Input id="rsvp-adults" data-testid="rsvp-adults" type="number" min={1} max={30} value={form.adults}
                onChange={(e) => setForm({ ...form, adults: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
            </div>
          )}
          {showChildren && (
            <div>
              <Label htmlFor="rsvp-children">{t("rsvp.children")}</Label>
              <Input id="rsvp-children" data-testid="rsvp-children" type="number" min={0} max={30} value={form.children}
                onChange={(e) => setForm({ ...form, children: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
            </div>
          )}
        </div>
      )}

      {parts.length > 0 && (
        <div data-testid="rsvp-parts" data-invalid={errors.parts ? "true" : undefined}>
          <Label className={errors.parts ? "text-red-500" : ""}>{PARTS_L.title[lang] || PARTS_L.title.en}<span className="text-[#BE923C]"> *</span></Label>
          <div className="mt-2 space-y-2">
            {parts.map((p) => {
              const on = selectedParts.includes(p.id);
              const label = (p.i18n?.[lang]?.label) || p.label;
              const when = [p.date ? euDate(p.date) : "", p.time || ""].filter(Boolean).join(" · ");
              const pa = Number(p.price_adult) || 0, pc = Number(p.price_child) || 0;
              const hasCap = Number(p.capacity) > 0 && typeof p.remaining === "number";
              const full = hasCap && p.full;
              return (
                <button key={p.id} type="button" data-testid={`rsvp-part-${p.id}`} aria-pressed={on} disabled={full}
                  onClick={() => { setSelectedParts((s) => (on ? s.filter((x) => x !== p.id) : [...s, p.id])); clearError("parts"); }}
                  className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-start transition-colors ${full ? "border-[#0F172A]/10 bg-[#F4F2EE] opacity-60 cursor-not-allowed" : on ? "border-[#BE923C] bg-[#FBF7EE]" : "border-[#0F172A]/10 bg-white hover:border-[#BE923C]/60"}`}>
                  <span className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${on ? "bg-[#BE923C] border-[#BE923C] text-white" : "border-[#0F172A]/25"}`}>{on && <CheckCircle2 size={14} />}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-[#0F172A]">{label}</span>
                    {when && <span className="block text-xs text-[#475569]">{when}</span>}
                    {hasCap && (
                      <span className={`block text-xs font-medium ${full ? "text-red-500" : "text-[#9E7829]"}`} data-testid={`rsvp-part-cap-${p.id}`}>
                        {full ? (PARTS_L.full[lang] || PARTS_L.full.en) : (PARTS_L.left[lang] || PARTS_L.left.en).replace("{n}", p.remaining)}
                      </span>
                    )}
                  </span>
                  {(pa > 0 || pc > 0) && <span className="text-xs font-semibold text-[#9E7829] whitespace-nowrap">{money(pa, cur)}{pc > 0 ? ` / ${money(pc, cur)}` : ""}</span>}
                </button>
              );
            })}
          </div>
          {errors.parts && <p className="mt-1 text-xs text-red-500" data-testid="rsvp-parts-error">{PARTS_L.required[lang] || PARTS_L.required.en}</p>}
        </div>
      )}

      {allFields.length > 0 && (
        <div data-testid="rsvp-custom-fields" className="space-y-4">
          {allFields.map(renderField)}
        </div>
      )}

      {isRequired && (
        <div className="rounded-2xl bg-[#FBF7EE] border border-[#BE923C]/25 p-4">
          {hasPer && hasFamily && (
            <div className="mb-3">
              <Label>{t("rsvp.ticket")}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" data-testid="ticket-per_person" onClick={() => setTicket("per_person")}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ticket === "per_person" ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                  {t("rsvp.per_person")}
                </button>
                <button type="button" data-testid="ticket-family" onClick={() => setTicket("family")}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ticket === "family" ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                  {t("rsvp.family")}<br /><span className="text-xs font-normal opacity-80">{money(pf, cur)}</span>
                </button>
              </div>
            </div>
          )}

          {ticket === "per_person" ? (
            <div className="space-y-1.5 text-sm text-[#475569]">
              {adultP > 0 && (
                <div className="flex items-center justify-between" data-testid="rsvp-line-adults">
                  <span>{t("rsvp.adults")} × {adults} <span className="opacity-70">({money(adultP, cur)} {t("rsvp.each")})</span></span>
                  <span className="font-medium text-[#0F172A]">{money(adults * adultP, cur)}</span>
                </div>
              )}
              {childP > 0 && (
                <div className="flex items-center justify-between" data-testid="rsvp-line-children">
                  <span>{t("rsvp.children")} × {children} <span className="opacity-70">({money(childP, cur)} {t("rsvp.each")})</span></span>
                  <span className="font-medium text-[#0F172A]">{money(children * childP, cur)}</span>
                </div>
              )}
            </div>
          ) : null}

          <div className="mt-3 pt-3 border-t border-[#BE923C]/20 flex items-center justify-between text-[#0F172A]">
            <span className="text-sm text-[#475569]">{t("rsvp.total")}</span>
            <span data-testid="rsvp-total" className="font-serif text-2xl font-semibold text-[#BE923C]">{money(amount, cur)}</span>
          </div>
        </div>
      )}

      {isSuggested && (
        <div className="rounded-2xl bg-[#FBF7EE] border border-[#BE923C]/25 p-4 space-y-3" data-testid="rsvp-suggested-box">
          {!event.hide_suggested_note && (
            <p className="text-sm text-[#0F172A] font-medium" data-testid="rsvp-suggested-note">
              {suggestedNote[lang] || suggestedNote.en || SUGGESTED_NOTE_DEFAULT[lang] || SUGGESTED_NOTE_DEFAULT.en}
            </p>
          )}
          {hasPer && hasFamily && (
            <div>
              <Label>{t("rsvp.ticket")}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" data-testid="ticket-per_person" onClick={() => { setTicket("per_person"); setContribTouched(false); }}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ticket === "per_person" ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                  {t("rsvp.per_person")}
                </button>
                <button type="button" data-testid="ticket-family" onClick={() => { setTicket("family"); setContribTouched(false); }}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ticket === "family" ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                  {t("rsvp.family")}
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label>{L.title}</Label>
            {amount > 0 && <span className="text-xs text-[#9E7829]" data-testid="rsvp-suggested-amount">{L.word}: {money(amount, cur)}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button key={p} type="button" data-testid={`contrib-preset-${p}`} onClick={() => setContrib(String(p))}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${Math.round(contribValue) === p ? "bg-[#BE923C] text-white border-[#BE923C]" : "bg-white text-[#0F172A] border-[#0F172A]/10 hover:border-[#BE923C]"}`}>
                {money(p, cur)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} step="1" inputMode="decimal" data-testid="contrib-amount"
              value={contribTouched ? contribution : (defaultContrib || "")}
              onChange={(e) => setContrib(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="border-[#0F172A]/10 max-w-[160px]" />
            <span className="text-sm text-[#475569] uppercase">{cur}</span>
          </div>
          <p className="text-xs text-[#475569]">{L.hint}</p>
        </div>
      )}

      <button type="submit" disabled={submitting} data-testid="rsvp-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white font-semibold py-3.5 transition-colors disabled:opacity-60">
        {payNow && <CreditCard size={18} />}
        {submitting && payLoading !== "paypal"
          ? (payNow ? t("donate.redirecting") : t("rsvp.confirming"))
          : isRequired ? `${t("rsvp.pay_card")} · ${money(amount, cur)}`
          : (isSuggested && contribValue > 0) ? `${L.contribute} · ${money(contribValue, cur)}`
          : t("rsvp.confirm")}
      </button>

      {isSuggested && contribValue > 0 && (
        <button type="button" data-testid="rsvp-without-btn" onClick={(e) => submit(e, true)} disabled={submitting}
          className="w-full text-sm font-semibold text-[#475569] hover:text-[#0F172A] underline underline-offset-4 disabled:opacity-60">
          {L.without}
        </button>
      )}

      {payNow && paypalEnabled && (
        <>
          <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
            <span className="h-px flex-1 bg-[#0F172A]/10" />
            {t("donate.or")}
            <span className="h-px flex-1 bg-[#0F172A]/10" />
          </div>
          <button type="button" onClick={payPaypal} disabled={submitting} data-testid="rsvp-paypal-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FFC439] hover:bg-[#f0b62d] text-[#003087] font-bold py-3.5 transition-colors disabled:opacity-60">
            {payLoading === "paypal"
              ? t("donate.redirecting")
              : <><span className="font-extrabold italic">Pay<span className="text-[#0070E0]">Pal</span></span></>}
          </button>
        </>
      )}
    </form>
  );
}
